import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Leaf, Snowflake, Sun, Flower2, TreePine, CloudRain, Wind, Sprout, Cherry, Droplets } from 'lucide-react';

function getSeason() {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'fall';
  return 'winter';
}

const seasonConfig = {
  spring: {
    icons: [Flower2, Sprout, Cherry, Droplets, Leaf],
    colors: ['#8FBC8F', '#FFB6C1', '#DDA0DD', '#98FB98', '#F0E68C'],
  },
  summer: {
    icons: [Sun, Leaf, Droplets, Wind, Flower2],
    colors: ['#FFD700', '#4A5D3A', '#87CEEB', '#F4A460', '#FF6347'],
  },
  fall: {
    icons: [Leaf, Wind, CloudRain, Leaf, Leaf],
    colors: ['#D2691E', '#CD853F', '#B8860B', '#8B4513', '#DAA520'],
  },
  winter: {
    icons: [Snowflake, TreePine, Snowflake, Wind, Snowflake],
    colors: ['#B0C4DE', '#4A5D3A', '#ADD8E6', '#E0E0E0', '#87CEEB'],
  },
};

const floatingElements = [
  { top: '8%', left: '3%', size: 28, duration: 7, delay: 0 },
  { top: '15%', right: '5%', size: 22, duration: 9, delay: 1.5 },
  { top: '35%', left: '6%', size: 18, duration: 6, delay: 0.5 },
  { top: '50%', right: '4%', size: 24, duration: 8, delay: 2 },
  { top: '65%', left: '4%', size: 20, duration: 10, delay: 1 },
  { top: '78%', right: '6%', size: 26, duration: 7, delay: 0.8 },
  { top: '25%', left: '92%', size: 16, duration: 8, delay: 3 },
  { top: '45%', left: '2%', size: 20, duration: 9, delay: 2.5 },
  { top: '88%', right: '3%', size: 22, duration: 6, delay: 1.2 },
  { top: '55%', left: '95%', size: 18, duration: 11, delay: 0.3 },
];

export default function SeasonalIcons() {
  const season = useMemo(() => getSeason(), []);
  const config = seasonConfig[season];

  return (
    <div
      style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 5, overflow: 'hidden' }}
      aria-hidden="true"
      data-testid="seasonal-icons"
    >
      {floatingElements.map((el, i) => {
        const Icon = config.icons[i % config.icons.length];
        const color = config.colors[i % config.colors.length];
        const style = { position: 'absolute', top: el.top };
        if (el.left) style.left = el.left;
        if (el.right) style.right = el.right;

        return (
          <motion.div
            key={i}
            style={style}
            initial={{ opacity: 0, y: 0, rotate: 0 }}
            animate={{
              opacity: [0, 0.15, 0.25, 0.15, 0],
              y: [0, -30, -15, -40, 0],
              x: [0, 10, -8, 12, 0],
              rotate: [0, 15, -10, 20, 0],
            }}
            transition={{
              duration: el.duration,
              delay: el.delay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <Icon size={el.size} color={color} strokeWidth={1} />
          </motion.div>
        );
      })}
    </div>
  );
}
